<template>
  <!-- Item de coleção: a régua à esquerda e o nome na cor de leitura. A cor é mancha, e não
       papel de texto — foi por isso que o chip saiu daqui um dia: tingir o fundo obriga o nome
       a escurecer, e aí a cor está num papel que não é dela. -->
  <span v-if="form === 'accent'" class="colored-value" :title="option.name">
    <ontos-color-accent vertical :color="option.color" class="ontos-rel-accent-list"/>
    <span class="colored-value-text">{{ option.name }}</span>
  </span>

  <!-- Pessoa: como ela aparece em todo o app, numa linha só — no menu divide a largura com a caixa
       de seleção. -->
  <ontos-person-line v-else-if="form === 'person' && option.person" dense
                     :user="option.person.user" :name="option.person.name" :photo="option.person.photo"/>

  <!-- Situação e opção de domínio: as duas formas que o ColoredOptionChip já desenha. -->
  <ontos-colored-option v-else :option="option" :dot="form === 'dot'"/>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ColorAccent from '@/components/shared/ColorAccent.vue'
import ColoredOptionChip from '@/components/shared/ColoredOptionChip.vue'
import PersonLine from '@/components/shared/PersonLine.vue'
import {ColoredOption, ValueForm} from '@/types/ColoredOption'

/**
 * Um valor colorido na forma que a natureza dele pede: régua, bolinha ou chip.
 *
 * As três já existiam, cada uma desenhada à mão onde fazia falta — e a escolha entre elas
 * também, repetida em cada tela que mostra valor de dimensão. O que faltava era o lugar onde a
 * pergunta "que natureza tem este valor?" se responde uma vez. Ver `ValueForm`, que é onde a
 * resposta está escrita.
 *
 * **Não redesenha nada**: a bolinha e o chip continuam saindo do `ColoredOptionChip`, e a régua
 * do `ColorAccent`. Daqui sai só a escolha, mais o arranjo da régua com o nome ao lado — que
 * era o único dos três que não tinha dono e aparecia copiado.
 *
 * A régua usa a classe global `ontos-rel-accent-list` porque dentro de um item de lista ela não
 * tem de onde tirar altura — o porquê está no `App.vue`, junto da regra.
 */
@Component({
  name: 'ontos-colored-value',
  components: {
    'ontos-color-accent': ColorAccent,
    'ontos-colored-option': ColoredOptionChip,
    'ontos-person-line': PersonLine
  }
})
class ColoredValue extends Vue {
  @Prop({type: Object as () => ColoredOption, required: true}) readonly option!: ColoredOption
  @Prop({type: String as () => ValueForm, required: true}) readonly form!: ValueForm
}

export default toNative(ColoredValue)
</script>

<style scoped>
/* align-center, e não stretch: a régua tem altura própria aqui (ver a classe global), e esticar
   dentro de uma linha de altura variável a faria crescer com o que estiver ao lado. */
.colored-value {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

/* O nome corta com reticências em vez de empurrar a linha: no menu de raias e no modal ele
   divide a largura com a caixa de seleção. */
.colored-value-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
